import React from 'react';
import contensis from 'Common/api/contensis';
import { Op, OrderBy } from 'contensis-delivery-api';
import { excludeMetadata } from 'Common/util/contensis/api';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';

import type { NextPageWithLayout, GetServerSideProps } from 'next';

import MainLayout from 'Layout/Main';
import Metadata from 'Components/Metadata';
import Template, { datamap, PageData } from 'Templates/Staff/Listing';

const StaffListing: NextPageWithLayout<PageData> = ({
    entry,
    ancestors,
    profiles,
    searchTerm,
    roleValue,
    departmentValue
}) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template
                {...datamap({
                    entry,
                    ancestors,
                    profiles,
                    searchTerm,
                    roleValue,
                    departmentValue
                })}
            />
        </>
    );
};

StaffListing.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ query, preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });

    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: '/staff',
            entryLinkDepth: 2,
            entryFields: ['*', ...excludeMetadata]
        });

        if (!resPage.entry) {
            return {
                error: true
            };
        }

        const entry = resPage.entry;

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        const formatFilter = (query: string | string[] | undefined): string => {
            if (typeof query === 'string') {
                return query.split('-').join('/');
            }
            if (Array.isArray(query)) {
                const joinedItems = query.join();
                return joinedItems.split('-').join('/');
            }
            return '';
        };

        const searchTerm = Array.isArray(query.search)
            ? query.search.join(' ')
            : query.search
            ? query.search
            : null;

        const searchTermQueries = searchTerm
            ? [
                  Op.contains('title', searchTerm).weight(100),
                  Op.freeText('pageMetadata.keywords', searchTerm).weight(25),
                  Op.freeText('pageMetadata.description', searchTerm).weight(25),
                  Op.contains('sys.contentTypeId', searchTerm).weight(10)
              ]
            : [];

        /* Role query */
        const roleValue = formatFilter(query.role);
        const rolesValueQueries = roleValue ? [Op.contains('role', roleValue).weight(100)] : [];

        /* Departments query */
        const departmentValue = formatFilter(query.department);
        const departmentsValueQueries = query.department
            ? [Op.contains('department', departmentValue).weight(100)]
            : [];

        const profilesQuery = [
            Op.or(
                Op.equalTo('sys.contentTypeId', 'academicStaffProfile'),
                Op.equalTo('sys.contentTypeId', 'governorStaffProfile'),
                Op.equalTo('sys.contentTypeId', 'professionalStaffProfile')
            ),
            Op.or(...searchTermQueries),
            Op.or(...rolesValueQueries),
            Op.or(...departmentsValueQueries),
            Op.exists('staffMember', true)
        ];

        /* Fetching profiles */
        const resProfiles = await search(profilesQuery, {
            pageSize: 10,
            pageIndex: parseInt(query.page as string) - 1 || 0,
            linkDepth: 3,
            orderBy: OrderBy.asc('title'),
            fields: [
                'staffMember',
                'person',
                'contact',
                'phoneNumber',
                'hideEmail',
                'email',
                'department',
                'name',
                'sys',
                'uri',
                'honorific',
                'forename',
                'surname',
                'jobTitle',
                'picture'
            ]
        });

        return {
            entry,
            ancestors,
            profiles: resProfiles,
            searchTerm,
            roleValue,
            departmentValue
        };
    });

    if (error) {
        return {
            notFound: true
        };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return {
            props: data
        };
    }
};

export default StaffListing;
